/**
 * useMarkers - Custom hook for Google Maps marker management
 * Handles marker creation, positioning, click handlers, and hover effects
 */
import { useEffect, useRef, useCallback, useState } from 'react';
import { createMarkerPopup } from './markerPopup';
import { createDispatchMarker } from '@/utils/dispatchMarker';
import { readCoordinatePair, readOrderCoordinate } from '@/utils/mapCoordinates';
import { 
  getOrderNumberSuffix, 
  createDualMarkerIcon, 
  createAvailableMarkerIcon, 
  createCommittedMarkerIcon,
  createDeliveredMarkerIcon,
  createDesignedMarkerIcon,
  createAwaitingDesignMarkerIcon
} from '@/utils/markerUtils';

/**
 * @param {Object} options
 * @param {google.maps.Map} options.map - Google Maps instance
 * @param {google.maps.InfoWindow} options.infoWindow - Shared InfoWindow instance
 * @param {Array} options.orders - Filtered orders for current date (with status field)
 * @param {Map} options.stopInfoMap - Map of orderId -> {sequence, orderSuffix}
 * @param {Object} options.selectedRun - Currently selected run
 * @param {boolean} options.hideCommittedOrders - Whether to hide committed order markers
 * @param {Function} options.onAddToRun - Callback when adding order to run
 * @param {Function} options.onRemoveFromRun - Callback when removing order from run
 * @param {Function} options.onRemoveFromCommittedRun - Callback for removing from committed run (shows dialog)
 * @param {string} options.driverColor - Optional hex color of assigned driver (e.g., "#EF4444")
 * 
 * Note: Order status is the single source of truth for all marker rendering.
 */
const useMarkers = ({
  map,
  infoWindow,
  orders,
  stopInfoMap,
  selectedRun,
  hideCommittedOrders,
  onAddToRun,
  onRemoveFromRun,
  onRemoveFromCommittedRun,
  onEditFlag,
  driverColor
}) => {
  const markersRef = useRef({});
  const hasInitialFitRef = useRef(false);
  const [markerError, setMarkerError] = useState(null);

  // Reset initial fit flag when orders change significantly
  const resetInitialFit = useCallback(() => {
    hasInitialFitRef.current = false;
  }, []);

  // Main marker creation and management effect
  // Note: This effect intentionally has many dependencies because markers must
  // be recreated atomically when ANY input changes (orders, run state, callbacks).
  // Splitting into multiple effects would cause half-updated marker states.
  useEffect(() => {
    setMarkerError(null);
    if (!map || !window.google || !infoWindow) return;

    // The previous effect cleanup owns and removes its marker instances.
    markersRef.current = {};

    // Group orders by location for offset positioning
    const locationGroups = new Map();
    const positionedOrders = orders.flatMap(order => {
      const coordinate = readOrderCoordinate(order);
      return coordinate ? [{ order, coordinate }] : [];
    });
    const locationKey = coordinate => `${coordinate.latitude.toFixed(6)},${coordinate.longitude.toFixed(6)}`;
    positionedOrders.forEach(item => {
      const key = locationKey(item.coordinate);
      if (!locationGroups.has(key)) {
        locationGroups.set(key, []);
      }
      locationGroups.get(key).push(item);
    });

    // Update marker positions based on zoom level (for grouped markers)
    const updateMarkerPositions = () => {
      if (!effectActive) return;
      locationGroups.forEach((ordersAtLocation) => {
        if (ordersAtLocation.length <= 1) return;
        
        const zoom = map.getZoom();
        const baseOffset = Math.max(0.00008, 0.001 / Math.pow(2, zoom - 10));
        
        ordersAtLocation.forEach(({ order, coordinate }, idx) => {
          const marker = markersRef.current[order.id];
          if (!marker) return;
          
          const angle = (2 * Math.PI * idx) / ordersAtLocation.length;
          const offset = baseOffset * (1 + idx * 0.2);
          const point = readCoordinatePair(
            coordinate.latitude + Math.sin(angle) * offset,
            coordinate.longitude + Math.cos(angle) * offset,
          ) || coordinate;
          marker.element.position = { lat: point.latitude, lng: point.longitude };
        });
      });
    };

    // A popup owns its nodes/listeners. Replacing or closing it invalidates
    // detached controls; callbacks never search the document for an order ID.
    let activePopup = null;
    let effectActive = true;
    const newMarkers = [];
    const mapListeners = [];
    const disposePopup = () => {
      activePopup?.dispose();
      activePopup = null;
    };
    const popupListeners = [
      infoWindow.addListener('closeclick', disposePopup),
      infoWindow.addListener('close', disposePopup),
      infoWindow.addListener('content_changed', () => {
        if (activePopup && infoWindow.getContent() !== activePopup.content) disposePopup();
      }),
    ];
    const showPopup = (order, stopInfo, hideActions, marker) => {
      if (!effectActive) return;
      disposePopup();
      const popup = createMarkerPopup({
        order, stopInfo, hideActions, selectedRun,
        onAddToRun, onRemoveFromRun, onRemoveFromCommittedRun, onEditFlag,
        isCurrent: () => effectActive && activePopup === popup && infoWindow.getContent() === popup.content,
        onClose: () => {
          // A callback may synchronously replace this popup. Do not close its successor.
          if (activePopup !== popup) return;
          const ownsContent = infoWindow.getContent() === popup.content;
          disposePopup();
          if (ownsContent) infoWindow.close();
        },
      });
      activePopup = popup;
      try {
        infoWindow.setContent(popup.content);
        if (activePopup === popup) infoWindow.open({ map, anchor: marker.element });
      } catch {
        popup.dispose();
        if (activePopup === popup) activePopup = null;
        setMarkerError('Dispatch details could not be displayed. Your orders and run drafts are unchanged.');
      }
    };

    const cleanup = () => {
      effectActive = false;
      popupListeners.forEach(listener => window.google.maps.event.removeListener(listener));
      mapListeners.forEach(listener => window.google.maps.event.removeListener(listener));
      const ownsContent = activePopup && infoWindow.getContent() === activePopup.content;
      disposePopup();
      if (ownsContent) infoWindow.close();
      newMarkers.forEach(marker => marker.dispose());
      markersRef.current = {};
    };

    try {
      // Create a complete generation; retire every partial instance on failure.
      positionedOrders.forEach(({ order, coordinate }) => {
      
        // Skip 'new' status orders - they haven't been accepted yet
        if (order.status === 'new') {
          return null;
        }
      
        // Check commitment status using order.status (source of truth)
        const isCommitted = order.status === 'on_route';
        const isDelivered = order.status === 'delivered';
        const isDesigned = order.status === 'designed';
        const isAccepted = order.status === 'accepted';
      
        // Skip committed orders if filter is active
        if (isCommitted && hideCommittedOrders) {
          return null;
        }
      
        const stopInfo = stopInfoMap.get(order.id);
        const orderSuffix = getOrderNumberSuffix(order.internal_order_number);
        const isFlagged = !!(order.dispatch_note && order.dispatch_note.trim());

        // Determine marker icon based on state
        // Priority: 1) In current run (numbered), 2) Delivered (light gray), 3) Committed (dark gray), 4) Status-based color
        let icon;
        if (stopInfo) {
          // In the currently selected run - numbered marker with driver color
          icon = createDualMarkerIcon(stopInfo.sequence, stopInfo.orderSuffix, false, driverColor, isFlagged);
        } else if (isDelivered) {
          // Delivered - light gray marker (faded)
          icon = createDeliveredMarkerIcon(orderSuffix, isFlagged);
        } else if (isCommitted) {
          // Committed to another run - dark gray marker
          icon = createCommittedMarkerIcon(orderSuffix, isFlagged);
        } else if (isDesigned) {
          // Ready to be assigned - purple marker
          icon = createDesignedMarkerIcon(orderSuffix, false, isFlagged);
        } else if (isAccepted) {
          // Awaiting design - amber/orange marker
          icon = createAwaitingDesignMarkerIcon(orderSuffix, false, isFlagged);
        } else {
          // Fallback (shouldn't happen, but just in case)
          icon = createAvailableMarkerIcon(orderSuffix, false, isFlagged);
        }

        // Create the marker
        const marker = createDispatchMarker({
          position: { lat: coordinate.latitude, lng: coordinate.longitude },
          map,
          icon,
          zIndex: stopInfo ? 100 + stopInfo.sequence : (isCommitted ? 50 : (isDelivered ? 40 : 1)),
          title: `Order #${orderSuffix}`,
          onContextMenu: () => {
            const hideActions = isCommitted || isDelivered;
            showPopup(order, stopInfo, hideActions, marker);
          },
          // Left-click: quick action (add/remove from run).
          onClick: () => {
            if (!effectActive) return;
            // Committed or delivered orders: show info only
            if (isCommitted || isDelivered) {
              showPopup(order, stopInfo, true, marker);
              return;
            }

            const inRun = stopInfo !== undefined;
            if (inRun) {
              if (selectedRun?.status !== 'draft') {
                onRemoveFromCommittedRun(order.id, order.recipient_name);
              } else {
                onRemoveFromRun(order.id);
              }
            } else if (selectedRun) {
              onAddToRun(order.id);
            }
          },
        });
        newMarkers.push(marker);
        const key = locationKey(coordinate);
        const siblings = locationGroups.get(key) || [];
        marker.siblingIds = siblings.map(item => item.order.id).filter(id => id !== order.id);
        markersRef.current[order.id] = marker;
      });

      // Fit map bounds on initial load
      if (newMarkers.length > 0 && !hasInitialFitRef.current) {
        const bounds = new window.google.maps.LatLngBounds();
        newMarkers.forEach(marker => bounds.extend(marker.element.position));
        map.fitBounds(bounds);
        hasInitialFitRef.current = true;

        // Don't zoom in too far for single markers
        if (newMarkers.length === 1) {
          mapListeners.push(window.google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
            if (effectActive && map.getZoom() > 15) map.setZoom(15);
          }));
        }
      }

      // Listen for zoom changes to reposition grouped markers
      mapListeners.push(map.addListener('zoom_changed', updateMarkerPositions));
    } catch {
      cleanup();
      setMarkerError('Dispatch pins could not be displayed. Your orders and run drafts are unchanged.');
    }

    // Cleanup
    return cleanup;
  }, [map, orders, infoWindow, stopInfoMap, selectedRun, hideCommittedOrders, onAddToRun, onRemoveFromRun, onRemoveFromCommittedRun, onEditFlag, driverColor]);

  // Hover highlighting effect
  const updateHoverHighlight = useCallback((hoveredStopId, hoveredAvailableOrderId) => {
    if (!window.google || Object.keys(markersRef.current).length === 0) return;

    const hoveredMarker = hoveredStopId ? markersRef.current[hoveredStopId] : 
                          hoveredAvailableOrderId ? markersRef.current[hoveredAvailableOrderId] : null;
    const siblingIds = hoveredMarker?.siblingIds || [];

    Object.entries(markersRef.current).forEach(([orderId, marker]) => {
      const stopInfo = stopInfoMap.get(orderId);
      const order = orders.find(o => o.id === orderId);
      const isFlagged = !!(order?.dispatch_note && order.dispatch_note.trim());
      const isHoveredStop = orderId === hoveredStopId;
      const isHoveredAvailable = orderId === hoveredAvailableOrderId;
      const isSiblingHovered = siblingIds.includes(orderId);
      const isHighlighted = isHoveredStop || isHoveredAvailable || isSiblingHovered;

      if (stopInfo !== undefined) {
        // Pass driver color for consistent coloring when not highlighted
        marker.setVisual(createDualMarkerIcon(stopInfo.sequence, stopInfo.orderSuffix, isHighlighted, driverColor, isFlagged));
        marker.element.zIndex = isHighlighted ? 1000 : 100 + stopInfo.sequence;

        if (isHoveredStop || isSiblingHovered) {
          marker.bounce();
        }
      } else {
        const orderSuffix = order ? getOrderNumberSuffix(order.internal_order_number) : '?';
        const isDelivered = order?.status === 'delivered';
        const isCommitted = order?.status === 'on_route';
        const isDesigned = order?.status === 'designed';

        let icon;
        if (isDelivered) {
          icon = createDeliveredMarkerIcon(orderSuffix, isFlagged);
        } else if (isCommitted) {
          icon = createCommittedMarkerIcon(orderSuffix, isFlagged);
        } else if (isDesigned) {
          icon = createDesignedMarkerIcon(orderSuffix, isHighlighted, isFlagged);
        } else {
          icon = createAwaitingDesignMarkerIcon(orderSuffix, isHighlighted, isFlagged);
        }
        
        marker.setVisual(icon);
        marker.element.zIndex = isHighlighted ? 1000 : (isCommitted ? 50 : (isDelivered ? 40 : 1));
        
        if (isHoveredAvailable || isSiblingHovered) {
          marker.bounce();
        }
      }
    });
  }, [stopInfoMap, orders, driverColor]);

  return {
    markersRef,
    markerError,
    resetInitialFit,
    updateHoverHighlight
  };
};

export default useMarkers;
